import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Check, X, CheckCircle2, XCircle, Printer } from "lucide-react";

const freqLabels = { one_time: "One-time", weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly" };

export default function PublicQuote() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState("");

  const load = () => api.get(`/public/quotes/${token}`)
    .then((r) => {
      setQuote(r.data);
      setName(r.data.response_name || r.data.customer_name || "");
      setResult(["accepted", "declined"].includes(r.data.status) ? r.data.status : "");
      if (params.get("print") === "1") setTimeout(() => window.print(), 900);
    })
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Quote not found"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const respond = async (action) => {
    if (action === "accept" && !name.trim()) return setError("Please type your name to approve.");
    setSubmitting(true); setError("");
    try { const { data } = await api.post(`/public/quotes/${token}/respond`, { action, name }); setResult(data.status); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (error && !quote)
    return <div className="min-h-screen flex items-center justify-center bg-background p-6"><div className="text-center"><XCircle className="h-10 w-10 text-destructive mx-auto mb-3" /><p className="text-muted-foreground">{error}</p></div></div>;
  if (!quote)
    return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const b = quote.business || {};

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4 print:bg-white print:py-0">
      <div className="max-w-2xl mx-auto bg-card border border-border rounded-lg overflow-hidden print:border-0 print:shadow-none">
        {/* Letterhead */}
        <div className="p-8 border-b-2 border-primary flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-16 w-16 object-contain" /> : <div className="h-14 w-14 rounded-md bg-primary flex items-center justify-center"><Leaf className="h-7 w-7 text-secondary" /></div>}
            <div>
              <div className="font-display font-black text-lg tracking-tight leading-tight">{b.name}</div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{b.address}<br />{b.phone} · {b.email}{b.license ? ` · Lic# ${b.license}` : ""}</div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="overline text-secondary">Quote</div>
            <div className="font-display font-bold">{quote.quote_number}</div>
            {quote.accepted_at && <div className="text-xs text-muted-foreground mt-1">{new Date(quote.accepted_at).toLocaleDateString()}</div>}
          </div>
        </div>

        <div className="p-8">
          <div className="flex justify-between text-sm mb-6">
            <div><div className="overline text-muted-foreground mb-1">Prepared for</div><div className="font-semibold">{quote.customer_name || "Valued Customer"}</div></div>
            <div className="text-right"><div className="overline text-muted-foreground mb-1">Service plan</div><div className="font-semibold">{freqLabels[quote.frequency] || quote.frequency}</div></div>
          </div>

          <table className="w-full text-sm mb-6">
            <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="py-2 font-medium">Description</th><th className="py-2 font-medium text-right">Amount</th></tr></thead>
            <tbody>
              {quote.lines.map((l, i) => (
                <tr key={i} className="border-b border-border/60">
                  <td className="py-2.5 pr-4">{l.description}</td>
                  <td className="py-2.5 text-right tabular-nums">{money(l.quantity * l.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end">
            <div className="w-full sm:w-64 space-y-1.5 text-sm">
              {quote.adjusted_subtotal !== quote.subtotal && <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="tabular-nums">{money(quote.subtotal)}</span></div>}
              {quote.tax > 0 && <div className="flex justify-between text-muted-foreground"><span>Tax ({quote.tax_rate}%)</span><span className="tabular-nums">{money(quote.tax)}</span></div>}
              <div className="flex justify-between font-display font-black text-2xl pt-2 border-t-2 border-primary"><span>Total</span><span className="tabular-nums text-primary">{money(quote.total)}</span></div>
            </div>
          </div>

          {quote.notes && <div className="mt-6 text-sm bg-muted/40 rounded-md p-4"><span className="font-semibold">Notes: </span>{quote.notes}</div>}
          {b.terms && <p className="mt-6 text-xs text-muted-foreground border-t border-border pt-4">{b.terms}</p>}
        </div>

        {/* Actions (hidden on print) */}
        <div className="px-8 pb-8 no-print">
          {result ? (
            <div className={`rounded-lg p-6 text-center ${result === "accepted" ? "bg-primary/10" : "bg-destructive/10"}`} data-testid="quote-result">
              {result === "accepted" ? <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" /> : <XCircle className="h-10 w-10 text-destructive mx-auto mb-3" />}
              <p className="font-display font-bold text-lg">{result === "accepted" ? "Quote approved — thank you!" : "Quote declined"}</p>
              <p className="text-sm text-muted-foreground mt-1">{result === "accepted" ? "We'll be in touch shortly to schedule your service." : "No problem — reach out if anything changes."}</p>
            </div>
          ) : (
            <div data-testid="quote-respond">
              <Label htmlFor="sig">Type your name to approve</Label>
              <Input id="sig" data-testid="quote-signature" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="mt-1.5 mb-4" />
              {error && <p className="text-sm text-destructive mb-3" data-testid="quote-error">{error}</p>}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button data-testid="accept-quote-btn" disabled={submitting} onClick={() => respond("accept")} className="flex-1 bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200"><Check className="h-4 w-4 mr-1.5" /> Approve Quote</Button>
                <Button data-testid="decline-quote-btn" disabled={submitting} variant="outline" onClick={() => respond("decline")} className="flex-1"><X className="h-4 w-4 mr-1.5" /> Decline</Button>
              </div>
            </div>
          )}
          <Button variant="ghost" onClick={() => window.print()} className="w-full mt-3" data-testid="print-btn"><Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF</Button>
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground mt-6 no-print">Powered by Neighborhood Lawn Care CRM</p>
    </div>
  );
}
